import { History, Loader2, Search, Trash2, X } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ru } from '@/i18n/ru';
import { cn } from '@/lib/cn';
import {
  clearAiSearchHistory,
  listAiSearchHistory,
  removeAiSearchHistory,
  type AiHistoryEntry,
} from '@/lib/aiSearchHistory';

export type SearchMode = 'files' | 'ai';

interface SearchBarProps {
  query: string;
  mode: SearchMode;
  loading?: boolean;
  onQueryChange: (q: string) => void;
  onModeChange: (mode: SearchMode) => void;
  onSubmitAi: () => void;
  onSelectAiHistory?: (entry: AiHistoryEntry) => void;
}

export function SearchBar({
  query,
  mode,
  loading,
  onQueryChange,
  onModeChange,
  onSubmitAi,
  onSelectAiHistory,
}: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<AiHistoryEntry[]>([]);

  const refreshHistory = () => setHistory(listAiSearchHistory());

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (historyOpen) refreshHistory();
  }, [historyOpen]);

  useEffect(() => {
    if (mode !== 'ai') setHistoryOpen(false);
  }, [mode]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'ai') onSubmitAi();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && mode === 'ai') {
      e.preventDefault();
      onSubmitAi();
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="relative flex min-w-0 flex-1 items-center gap-2"
      role="search"
      data-testid="search-bar"
    >
      <div
        className="inline-flex h-8 shrink-0 border border-border bg-bg p-0.5"
        role="tablist"
        aria-label={ru.searchMode}
      >
        {(['files', 'ai'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => onModeChange(m)}
            className={cn(
              'h-full px-2.5 text-[11px] uppercase tracking-wider',
              mode === m
                ? 'bg-surface-2 text-text'
                : 'text-text-muted hover:text-text',
            )}
          >
            {m === 'files' ? ru.files : ru.ai}
          </button>
        ))}
      </div>
      <div className="relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
        <Input
          ref={inputRef}
          id="global-search"
          data-testid="search-input"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={mode === 'ai' ? ru.searchDescribe : ru.searchFilenames}
          className="pl-7 pr-20"
          autoComplete="off"
          spellCheck={false}
        />
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] text-text-muted">
          ⌘K
        </span>
      </div>
      {mode === 'ai' ? (
        <>
          <Button
            type="button"
            size="sm"
            variant={historyOpen ? 'outline' : 'ghost'}
            aria-label={ru.aiHistory}
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((o) => !o)}
            data-testid="ai-history-toggle"
          >
            <History className="h-3.5 w-3.5" />
          </Button>
          <Button type="submit" variant="accent" size="sm" disabled={!query.trim() || loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {ru.search}
          </Button>
        </>
      ) : null}

      {mode === 'ai' && historyOpen ? (
        <div
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 max-h-72 overflow-auto border border-border bg-surface shadow-lg"
          data-testid="ai-history-panel"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
            <span className="text-[11px] uppercase tracking-wider text-text-muted">
              {ru.aiHistory}
            </span>
            <div className="flex items-center gap-1">
              {history.length > 0 ? (
                <button
                  type="button"
                  className="text-[11px] text-text-muted hover:text-danger"
                  onClick={() => {
                    clearAiSearchHistory();
                    refreshHistory();
                  }}
                >
                  {ru.aiHistoryClear}
                </button>
              ) : null}
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center text-text-muted hover:text-text"
                aria-label={ru.close}
                onClick={() => setHistoryOpen(false)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          {history.length === 0 ? (
            <p className="px-3 py-4 text-xs text-text-muted">{ru.aiHistoryEmpty}</p>
          ) : (
            <ul className="py-1">
              {history.map((entry) => (
                <li
                  key={`${entry.q}-${entry.at}`}
                  className="group flex items-center gap-1 px-1 hover:bg-row-hover"
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 px-2 py-1.5 text-left"
                    onClick={() => {
                      onSelectAiHistory?.(entry);
                      setHistoryOpen(false);
                    }}
                  >
                    <span className="block truncate text-xs text-text">{entry.q}</span>
                    <span className="block font-mono text-[10px] text-text-muted">
                      {entry.response.results.length} · {ru.aiHistorySaved} ·{' '}
                      {new Date(entry.at).toLocaleString('ru-RU')}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center text-text-muted opacity-0 hover:text-danger group-hover:opacity-100"
                    aria-label={ru.removeFromHistory}
                    onClick={() => {
                      removeAiSearchHistory(entry.q);
                      refreshHistory();
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </form>
  );
}
